import {
  addDaysToDate,
  getInitialValues,
  requiredMessage,
  today,
  onlyNumbersInput,
  statusList,
  toUTCISOStringWithCurrentTime,
} from "@/lib/utils";
import AuthService from "@/services/api/auth/auth";
import React, { useEffect, useMemo, useState } from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import toast from "@/lib/toast";
import MapView from "@/components/Map/MapView";
import { useDrawer } from "@/components/Drawer/context/DrawerContext";
import Modal from "@/components/Modal/Modal";
import { Typography } from "@mui/material";
import Pagination from "@/components/Table/Pagination";
import formRenderer from "@/lib/formRenderer";
import Table from "@/components/Table/Table";
import { Tabs, Tab, Box } from "@mui/material";
import CustomHeader from "@/components/CustomHeader/CustomHeader";
import { useSearchPagination } from "@/hooks/useSearchPagination";
import { createOrderColumns } from "./component/OrderColumn";
import styles from "./Order.module.scss"
import Filter from "@/components/FilterComponent/Filter";
import AppliedFilter from "@/components/FilterComponent/AppliedFilter";
import { DropdownService } from "@/services/dropdowns/dropdown.service";

interface RetilerRow {
  RetailerShopName: string;
  PersonName: string;
  Designation: string;
  Email: string;
  PhoneNumber: string;
  Action: string;
}

interface OrderFormType {
  RetailerName?: string;
  orderDate?: string;
  expectedDeliveryDate?: string;
  Product: {
    productCode?: string;
    quantity?: string;
    availableQty?: string;
  }[];
}

const ORDER_TABS = [
  { key: "AllOrders", label: "All Orders" },
  { key: "Requested", label: "Requested" },
  { key: "Approved", label: "Approved" },
  { key: "PartialDelivered", label: "Partial Delivered" },
  { key: "Delivered", label: "Delivered" },
  { key: "Declined", label: "Declined" },
];

const Order: React.FC = () => {
  const { openDrawer } = useDrawer();
  const [loading, setLoading] = useState(true);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [Localities, setLocalities] = useState<any[]>([]);
  const [attendanceData, setAttendanceData] = useState<RetilerRow[]>([]);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const handleCloseModal = () => setModalOpen(false);
  const [countries, setCountries] = useState<any[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState("AllOrders");
  const [RejectModal, setRejectModal] = useState(false);
  const [retailerOption, setretailerOption] = useState([]);
  const [rejectReason, setRejectReason] = useState("");
  const [productCodeOption, setproductCodeOption] = useState<any[]>([]);
  const [orderDateValue, setOrderDateValue] = useState<string>("");
  //Approved tab action button state
  const [deliverModalOpen, setDeliverModalOpen] = useState(false);
  const [selectedOrderForDelivery, setSelectedOrderForDelivery] = useState<any>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [qtyMessages, setQtyMessages] = useState<Record<number, string>>({});

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [filters, setFilters] = useState<{ status?: string }>({});

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const StatusCheck = async (
    action: "ACCEPT" | "REJECT",
    row: any,
    rejectionReason?: string
  ) => {
    if (action === "REJECT" && !rejectionReason) {
      setSelectedRow(row);
      setRejectModal(true);
      return;
    }

    const payload = {
      orderId: row.id,
      rejectionReason: action === "REJECT" ? rejectionReason : "",
      isApproved: action === "ACCEPT" ? true : false,
    };

    try {
      const res = await AuthService.OrderStatus(payload);

      if (res?.message === "Success") {
        toast.success(
          action === "ACCEPT" ? "Order Approved" : "Order Rejected"
        );
        setRejectModal(false);
        rejectReset();
        setRejectReason("");
        FetchAllOrders();
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch (err: any) {
      const errorMessage =
        err?.error || err?.message || "Something went wrong!";
      toast.error(errorMessage);
    }
  };

  const FetchAllOrders = async () => {
    setLoading(true);

    try {
      const config = TAB_CONFIG[activeTab];
      if (!config) return;

      const data = {
        from,
        to,
        search: debouncedSearch || "",
        status: filters.status ? filters.status : config.status,
      };

      const res = await AuthService.GetOrders(data);
      setTotalRecords(res?.data?.totalRecords);

      if (res?.status === 417 || !res?.data) {
        setAttendanceData([]);
        setTotalRecords(0);
        return;
      } else {

        const item = res?.data?.items.map((item: any) => ({
          id: item.id,
          orderNumber: item.orderNumber,
          orderDate: item.orderDate,
          retailerName: item.retailerName,
          companyName: item.companyName,
          totalQuantity: item.totalQuantity,
          expectedDeliveryDate: item.expectedDeliveryDate,
          retailerEmail: item.email,
          retailer: item.retailerName,
          comments: item.comments,
          shippingAddress: item.shippingAddress,
          orderDetails: item.orderDetails,
          status: item.status,
          distributorOrderNumber : item.distributorOrderNumber,
        }));

        setAttendanceData(item || []);
        setTotalRecords(res?.data?.totalRecords || 0);
      }
      setIsInitialLoad(false);
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => createOrderColumns(openDrawer, StatusCheck, FetchAllOrders),
    [openDrawer, FetchAllOrders]
  );

  const TAB_CONFIG: any = {
    AllOrders: {
      status: "",
      columns: columns.AllOrders,
    },

    Requested: {
      status: "Pending",
      columns: columns.requestedColumns,
    },
    Approved: {
      status: "Approved",
      columns: columns.approvedColumns,
    },
    PartialDelivered: {
      status: "PartialDelivered",
      columns: columns.partaildeliveredColumns,
    },
    Delivered: {
      status: "Delivered",
      columns: columns.deliveredColumns,
    },
    Declined: {
      status: "Rejected",
      columns: columns.declinedColumns,
    },
  };

  const handleOpenModal = async (param? : any) => {
    const response = await AuthService.GetRetailerOption();
    const res = await DropdownService.productsMaster(param);
    const options = response?.data?.map((item: any) => ({
      label: item.name,
      value: item.id,
    }));

    // const prodoptions = res?.data?.map((item: any) => ({
    //   label: item.productName,
    //   value: item.productId,
    //   availableQty: item.qtyDistributed,
    // }))

    setproductCodeOption(res?.options);
    setretailerOption(options);

    reset({
      ...getInitialValues(formConfig),
      orderDate: today,
      Product: [{ productCode: "", quantity: "" }],
    });
    setOrderDateValue(today);
    setSelectedRow(null);
    setModalOpen(true);
  };

  const formConfig = useMemo(
    (): Record<string, any> => ({
      RetailerName: {
        defaultValue: "",
        validations: { required: requiredMessage("Retailer Name") },
        controlType: "select",
        placeholder: "Enter Retailer Name",
        label: "Retailer Name",
        isRequired: true,
        options: retailerOption,
        isControlled: true,
      },
      orderDate: {
        defaultValue: "",
        validations: { required: requiredMessage("Order Date") },
        controlType: "date",
        placeholder: "Enter orderDate",
        label: "Order Date",
        isRequired: true,
        isDisabled : true,
        props: {
          min: today,
        },
      },
      expectedDeliveryDate: {
        defaultValue: "",
        validations: { required: requiredMessage("Expected Delivery Date") },
        controlType: "date",
        placeholder: "Enter expected Delivery Date",
        label: "Expected Delivery Date",
        isRequired: true,
        isDisabled: !orderDateValue,
        props: {
          min: orderDateValue ? addDaysToDate(orderDateValue, 0) : undefined,
        },
      },
    }),
    [retailerOption, orderDateValue]
  );

  const getProductRowConfig = (index: number) => {

    const currentSelected = watchedProducts[index]?.productCode;

    const filteredOptions = productCodeOption?.filter(
      (option: any) =>
        !selectedProductCodes.includes(option.value) ||
        option.value === currentSelected
    );

    return {
      [`Product.${index}.productCode`]: {
        controlType: "select",
        label: "Product Code",
        placeholder: "Select Product Code",
        validations: { required: "Product code is required" },
        options: filteredOptions,
        showLabel: true,
        isRequired: true,
        isControlled: true,
      },
      [`Product.${index}.quantity`]: {
        controlType: "input",
        label: "Qty",
        placeholder: "Enter Quantity",
        validations: {
          required: "Quantity required",
          // validate: (value: any) => {
          //   const product = productCodeOption.find(
          //     (p: any) =>
          //       Number(p.value) === Number(watchedProducts[index]?.productCode)
          //   );

          //   const originalAvailable = Number(product?.availableQty) || 0;
          //   const entered = Number(value) || 0;

          //   if (entered > originalAvailable) {
          //     return `Cannot exceed available qty`;
          //   }
          //   return true;
          // }
          validate: (value : any) => {
          if (value <= 0)
            return "Quantity must be greater than 0";

          if (value.toString().length > 10)
            return "Quantity cannot exceed 10 digits";

          return true;
        },
        },
        props: {
          type: "number",
          onKeyDown: onlyNumbersInput,
        },
        showLabel: true,
        isRequired: true,
        helperText: qtyMessages[index],
      },
      [`Product.${index}.availableQty`]: {
        controlType: "input",
        label: "Available Qty",
        isControlled: true,
        isDisabled: true,
        props: {
          type: "number",
        },
      },
    };
  };


  const { register, handleSubmit, control, watch, formState, setValue, reset } =
    useForm<OrderFormType>({
      defaultValues: {
        ...getInitialValues(formConfig),
        Product: [{ productCode: "", quantity: "", availableQty : "" }],
      },
      mode: "onChange",
    });

  const {
    register: rejectRegister,
    handleSubmit: rejectHandleSubmit,
    formState: rejectFormState,
    reset: rejectReset,
  } = useForm<{ rejectReason: string }>({
    defaultValues: {
      rejectReason: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "Product",
  });

  const watchedProducts = useWatch({
    control,
    name: "Product",
  }) || [];
  const selectedProductCodes = watchedProducts
    .map((p: any) => p?.productCode)
    .filter(Boolean);

  const selectedType: any = watch("type");
  // const selectedCountryId : any= watch("countryId");
  // const selectedStateId : any = watch("stateId");
  // const selectedCityId : any= watch("cityId");
  const watchedOrderDate: any = watch("orderDate");

  // Sync orderDateValue state with watched form value
  useEffect(() => {
    setOrderDateValue(watchedOrderDate || "");
  }, [watchedOrderDate]);

  const {
    debouncedSearch,
    search,
    setSearch,
    pageSelected,
    setPageSelected,
    rowsPerPageValue,
    setRowsPerPageValue,
  } = useSearchPagination();

  const from = (pageSelected - 1) * rowsPerPageValue + 1;
  const to = pageSelected * rowsPerPageValue;
  const isSearchActive = debouncedSearch && debouncedSearch.trim() !== "";


  useEffect(() => {
    FetchAllOrders();
  }, [pageSelected, debouncedSearch, activeTab, filters.status]);

  useEffect(() => {
    setFilters({});
    setPageSelected(1);
  }, [activeTab]);

  const onSubmit = async (values: any) => {

    const payload = {
      retailerId: Number(values?.RetailerName),
      orderDate: toUTCISOStringWithCurrentTime(values.orderDate),
      expectedDeliveryDate: toUTCISOStringWithCurrentTime(values.expectedDeliveryDate),
      products:
        values.Product?.map((p: any) => ({
          productId: p.productCode ?? 0,
          quantity: Number(p.quantity) ?? 0,
        })) || [],
    };

    try {
      const res = await AuthService.AddOrders(payload);
      if (res?.status === 200) {
        toast.success(res?.message || "Retailer added successfully");
        setModalOpen(false);
        FetchAllOrders();
      } else if (res.status === 417){
        toast.error(res?.error || "Something went wrong");
      }
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.error ||
        error?.message ||
        "Something went wrong";
      toast.error(backendMessage);
    } finally {
    }
  };

  const dynamicFormConfig = (() => {
    const config: any = { ...formConfig };

    if (selectedType === "Retailer") {
      const entries = Object.entries(config);

      const typeIndex = entries.findIndex(([key]) => key === "type");

      const distributorField: [string, any] = [
        "DistributorCode",
        {
          defaultValue: "",
          validations: { required: requiredMessage("Distributor Code") },
          controlType: "input",
          label: "Distributor Code",
          isRequired: true,
        },
      ];

      // Insert DistributorCode after "type"
      entries.splice(typeIndex + 1, 0, distributorField);

      // Convert back to object
      return Object.fromEntries(entries);
    }

    return config;
  })();

  const handleDeleteConfirm = async (selectedRow: any) => {
    try {
      const res = await AuthService.DeleteOrder(selectedRow);
      if (res?.status === 200 && res?.data === true) {
        toast.success(res?.message || "Deleted successfully");
        setDeleteModalOpen(false);
        FetchAllOrders();
      } else {
        toast.error(res?.error || "Something went wrong");
      }
    } catch (error: any) {

      const backendMessage =
        error?.response?.data?.error ||
        error?.message ||
        "Something went wrong";

      toast.error(backendMessage);
    }
  };

  const currentConfig = TAB_CONFIG[activeTab];

  useEffect(() => {

  if (!watchedProducts?.length) return;

  watchedProducts.forEach((product: any, index: number) => {

    if (!product?.productCode) return;

    const selectedProduct = productCodeOption.find(
      (p) => Number(p.value) === Number(product.productCode)
    );

    if (!selectedProduct) return;

    const originalQty = Number(selectedProduct.availableQty) || 0;
    const enteredQty = Number(product.quantity) || 0;

    const remainingQty : any = originalQty - enteredQty >= 0
      ? originalQty - enteredQty
      : 0;

    if (Number(product.availableQty) !== remainingQty) {

      setValue(
        `Product.${index}.availableQty`,
        remainingQty,
        {
          shouldValidate: false,
          shouldDirty: false,
        }
      );

    }
  });

}, [watchedProducts, productCodeOption]);

  return (
    <>
      <div className="p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4"> 
          {isInitialLoad && totalRecords === 0 && !isSearchActive && activeTab === "AllOrders" && !filters.status ? (
            <></>
          ) : (
            <>
              <div className="flex items-center justify-between w-full">
                <h2 className="text-2xl md:text-lg font-semibold whitespace-nowrap">
                  Sales Orders
                </h2>

                {activeTab === "AllOrders" &&(
                <Filter
                  anchorEl={anchorEl}
                  onClick={handleFilterClick}
                  onClose={handleClose}
                  onApply={(appliedFilters) => {
                    setFilters({
                      status: appliedFilters.status || "",
                    });
                    setPageSelected(1);
                  }}
                  appliedFilter={filters}
                  showFilter={{ status: true }}
                  list={{ statusList: statusList }}
                />
                )}
              </div>
              <div className="flex gap-2 w-full md:w-auto md:items-center">
                <div className="flex-1 md:flex-none md:w-80">
                  <CustomHeader
                    search={search}
                    setSearch={setSearch}
                  />
                </div>

                <button
                  onClick={handleOpenModal}
                  className="cursor-pointer bg-indigo-500 text-white px-4 py-2 rounded-lg whitespace-nowrap"
                >
                  Add Order
                </button>
              </div>
            </>
          )}
        </div>

        {/* Show Applied Filter */}
        <AppliedFilter filterData={filters} onClearFilter={setFilters} />

        {loading ? (
          <div className="flex justify-center mt-20">
            Loading...
          </div>
        ) : isInitialLoad && totalRecords === 0 && !isSearchActive && activeTab === "AllOrders" && !filters.status ? (
          <div className="flex flex-col items-center justify-center mt-20">
            <img
              src="/assets/media/shopping_9725756.png"
              alt="No Retailer added yet"
              className="w-40 h-40 mb-4"
            />
            <h3 className="text-lg font-semibold mb-2">No Order added yet</h3>
            <p className="text-gray-500 mb-4">
              Add a Order and kickstart operations
            </p>
            <button
              onClick={handleOpenModal}
              className="px-4 py-2 bg-blue-600 text-white rounded-md cursor-pointer"
            >
              Add Order
            </button>
          </div>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                textColor="primary"
                indicatorColor="primary"
                allowScrollButtonsMobile
                scrollButtons="auto"
                variant="scrollable"
              >
                {ORDER_TABS.map((tab) => (
                  <Tab key={tab.key} label={tab.label} value={tab.key} />
                ))}
              </Tabs>
            </Box>
            <Table columns={currentConfig.columns} data={attendanceData} />

            <Pagination
              isPagination={true}
              totalCount={totalRecords ?? 0}
              pageSelected={pageSelected}
              setPageSelected={setPageSelected}
              rowsPerPageValue={rowsPerPageValue}
              setRowsPerPageValue={setRowsPerPageValue}
              customRowsPerPage={false}
            />
          </>
        )}

        <Modal
          modalOpen={modalOpen}
          closeModal={handleCloseModal}
          heading="Add Order"
          saveButtonText="Add"
          onSave={handleSubmit(onSubmit)}
          width="600px"
          isSubmitting={formState.isSubmitting}
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {formRenderer(dynamicFormConfig, formState, register, control)}
            </div>

            <div className="mt-4">
              <h4 className="text-xl font-semibold mb-4">Add Products</h4>
              {fields.map((field, index) => {
                const row = watchedProducts[index];
                const isRowFilled = row?.productCode && row?.quantity;
                const isLastRow = index === fields.length - 1;
                const canDelete = fields.length > 1;
                const showAddButton = isLastRow && isRowFilled;

                return (
                  <div key={field.id} >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {formRenderer(
                        getProductRowConfig(index),
                        formState,
                        register,
                        control
                      )}
                    </div>
                    
                    <div className={styles.rowActions}>
                      {showAddButton && (
                        <span
                          className={styles.addMore}
                          onClick={() =>
                            append({
                              productCode: "",
                              quantity: "",
                              availableQty : ""
                            })
                          }
                        >
                          + Add more
                        </span>
                      )}

                      {canDelete && (
                        <span
                          className={styles.deleteRow}
                          onClick={() => remove(index)}
                        >
                          Delete
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </form>
        </Modal>

        <Modal
          modalOpen={deleteModalOpen}
          closeModal={() => setDeleteModalOpen(false)}
          heading="Delete Order"
          width="400px"
          hideHeader={true}
          hideFooter={true}
        >
          <div className="flex flex-col items-center text-center p-4">
            <img src="/assets/media/Delete.png" className="w-14 mb-3" />

            <h2 className="text-lg font-semibold mb-2">Delete Order</h2>
            <p className="text-gray-500 mb-6">
              Are you sure you want to delete this Order?
            </p>

            <div className="flex gap-4 justify-center">
              <button
                className="px-6 py-2 border rounded-md"
                onClick={() => setDeleteModalOpen(false)}
              >
                Cancel
              </button>

              <button
                className="px-6 py-2 text-white rounded-md"
                style={{ backgroundColor: "#6570FC" }}
                onClick={() => handleDeleteConfirm(selectedRow?.id)}
              >
                Delete
              </button>
            </div>
          </div>
        </Modal>

        {/* accept reject */}
        <Modal
          modalOpen={RejectModal}
          closeModal={() => {
            setRejectModal(false);
            rejectReset();
          }}
          heading={"Reason for Rejection"}
          saveButtonText={"Reject"}
          onSave={rejectHandleSubmit((data) =>
            StatusCheck("REJECT", selectedRow, data.rejectReason)
          )}
          width="500px"
          isSubmitting={rejectFormState.isSubmitting}
        >
          <>
            <Typography mb={1}>
              Please provide reason for rejecting{" "}
              <strong>
                {selectedRow?.Name} <span style={{ color: "red" }}>*</span>
              </strong>
              :
            </Typography>

            <textarea
              {...rejectRegister("rejectReason", {
                required: "Reason is required",
              })}
              placeholder="Enter reason..."
              style={{
                width: "100%",
                minHeight: "80px",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
              }}
            />

            {rejectFormState.errors.rejectReason && (
              <p style={{ color: "red" }}>
                {rejectFormState.errors.rejectReason.message}
              </p>
            )}
          </>
        </Modal>
      </div>
    </>
  );
};

export default Order;
